import { Typography } from '@material-ui/core';
import { Inline, Stack, useSnackbarStack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { ReactElement, useState } from 'react';
import { trackEvent } from 'shared/helpers/Analytics';
import { ConfirmDialog } from 'shared/ui/ConfirmDialog';
import styled from 'styled-components';
import { Heading } from '../../core/Heading';
import { PostingListPagination } from '../../core/postings/PostingListPagination';
import { PostingsPageState } from '../../core/postings/PostingsPageState';
import { EmptySavedLoads } from '../../core/saved-loads/EmptySavedLoads';
import { usePostingsCount } from '../../data/LoadboardPostingAPI';
import {
  useDeleteAllSavedLoadsMutation,
  useDeleteUnavailableSavedLoadsMutation,
} from '../../data/LoadboardSavedLoadsAPI';
import { PostingListCard } from './PostingListCard';

const Wrapper = styled.div`
  flex: 1 1;
  display: flex;
  flex-direction: column;
  scroll-behavior: smooth;
`;

const Styled2 = styled.div`
  flex: 1;
`;

export function SavedLoadsList({
  postings,
  postingsPageError,
  postingsPagination,
  isPostingsPageLoading,
}: PostingsPageState): ReactElement {
  const { addSnackbar } = useSnackbarStack();
  const { data: postingsCount } = usePostingsCount();
  const [isConfirmRemoveAll, setConfirmRemoveAll] = useState(false);

  const deleteUnavailableSavedLoads = useDeleteUnavailableSavedLoadsMutation({
    onSuccess() {
      trackEvent('SLB: Clicked Remove Unavailable Loads');
      addSnackbar('All unavailable loads removed.', { variant: 'success' });
    },
    onError(error) {
      addSnackbar(error.message || 'Failed to remove unavailable loads.', {
        variant: 'error',
      });
    },
  });
  const deleteAllSavedLoads = useDeleteAllSavedLoadsMutation({
    onSuccess() {
      setConfirmRemoveAll(false);
      trackEvent('SLB: Clicked Remove All Saved Loads');
      addSnackbar('All loads were removed.', { variant: 'success' });
    },
    onError(error) {
      addSnackbar(error.message || 'Failed to remove all loads.', {
        variant: 'error',
      });
    },
  });

  return (
    <Wrapper>
      {postingsPageError ? (
        <Heading>Oops! Something Went wrong.</Heading>
      ) : !postings || isPostingsPageLoading ? (
        <Heading>Loading…</Heading>
      ) : postings.length === 0 ? (
        <EmptySavedLoads />
      ) : (
        <>
          <Box padding="small" paddingBottom="xsmall">
            <Stack>
              <Typography color="textSecondary">
                Unavailable loads will automatically be removed after 3 days.
              </Typography>

              <Inline>
                <Button
                  variant="text"
                  pending={deleteAllSavedLoads.isPending}
                  onClick={() => {
                    setConfirmRemoveAll(true);
                  }}
                >
                  Remove All
                </Button>

                <Button
                  variant="neutral"
                  pending={deleteUnavailableSavedLoads.isPending}
                  disabled={
                    // Posting count shows total available saved loads
                    postingsCount?.saved === postingsPagination?.count
                  }
                  onClick={() => {
                    deleteUnavailableSavedLoads.mutate();
                  }}
                >
                  Remove Unavailable
                </Button>
              </Inline>
            </Stack>
          </Box>

          <Styled2>
            {postings.map((item) => (
              <Box key={item.load.guid} margin="xsmall">
                <PostingListCard
                  posting={item}
                  suggestedLoadOpenSource="Saved Tab"
                />
              </Box>
            ))}
          </Styled2>
        </>
      )}

      <PostingListPagination pagination={postingsPagination} />

      <ConfirmDialog
        open={isConfirmRemoveAll}
        title="Remove All Saved Loads?"
        confirmButtonProps={{
          children: 'Remove All',
          pending: deleteAllSavedLoads.isPending,
          onClick: () => {
            deleteAllSavedLoads.mutate();
          },
        }}
        onClose={() => {
          setConfirmRemoveAll(false);
        }}
      />
    </Wrapper>
  );
}
